import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import DataTable from "../components/ui/DataTable";
import FormField from "../components/ui/FormField";
import TabBar from "../components/ui/TabBar";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";

const TABS = [
  { id: "list", label: "Material Types" },
  { id: "add", label: "Add Type" },
];

export default function Materials() {
  const [tab, setTab] = useState("list");
  const { refreshToken } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    material_name: "",
    category: "",
    unit: "",
    stock_qty: "",
    hsn_code: "",
    description: "",
  });

  const token = sessionStorage.getItem("maaze_token");

  /* ================= FETCH MATERIALS ================= */

  const fetchMaterials = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/materials",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setMaterials(res.data?.data || []);
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  /* ================= SAVE (POST / PUT) ================= */

  const handleSubmit = async () => {
    const payload = {
      ...form,
      stock_qty: Number(form.stock_qty),
    };

    if (editingId) {
      await axios.put(
        `https://maize.norissolutions.in/Php/public/api/materials/${editingId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } else {
      await axios.post(
        "https://maize.norissolutions.in/Php/public/api/materials",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }

    resetForm();
    setEditingId(null);
    setTab("list");
    fetchMaterials();
  };

  /* ================= EDIT ================= */

  const handleEdit = (row) => {
    setForm({
      material_name: row.material_name,
      category: row.category,
      unit: row.unit,
      stock_qty: row.stock_qty,
      hsn_code: row.hsn_code,
      description: row.description,
    });

    setEditingId(row.material_id);
    setTab("add");
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;

    await axios.delete(
      `https://maize.norissolutions.in/Php/public/api/materials/${id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchMaterials();
  };

  /* ================= RESET ================= */

  const resetForm = () => {
    setForm({
      material_name: "",
      category: "",
      unit: "",
      stock_qty: "",
      hsn_code: "",
      description: "",
    });
  };

  /* ================= TABLE COLUMNS ================= */

  const COLUMNS = [
    {
      key: "material_id",
      label: "ID",
      render: (v) => (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
          {v}
        </span>
      ),
    },
    {
      key: "material_name",
      label: "Material",
      render: (v) => <strong style={{ color: "var(--text)" }}>🌾 {v}</strong>,
    },
    {
      key: "category",
      label: "Category",
      render: (v) => <Badge color="sky">{v}</Badge>,
    },
    { key: "unit", label: "Unit" },
    {
      key: "is_active",
      label: "Status",
      render: (v) => (
        <Badge color={v === 1 ? "green" : "gray"}>
          {v === 1 ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "_a",
      label: "Actions",
      render: (_, row) => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            ✏️
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.material_id)}
          >
            🗑
          </Button>
        </>
      ),
    },
  ];

  /* ================= UI ================= */

  return (
    <div>
      <PageHeader
        title="Material Types"
        subtitle="Define seed and goods categories used across the system"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === "list" && (
        <Card
          title="All Material Types"
          action={
            <Button size="sm" onClick={() => setTab("add")}>
              + Add Type
            </Button>
          }
          noPad
        >
          <DataTable columns={COLUMNS} rows={materials} />
        </Card>
      )}

      {tab === "add" && (
        <Card title="Add Material Type">
          <div className="form-grid">
            <FormField
              label="Material Name"
              value={form.material_name}
              onChange={(e) =>
                setForm({ ...form, material_name: e.target.value })
              }
            />

            <FormField
              label="Category"
              type="text"
              list="category-options"
              value={form.category}
              placeholder="Select or type category"
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />

            <datalist id="category-options">
              <option value="Seed" />
              <option value="Fertilizer" />
              <option value="Pesticide" />
              <option value="Packaging" />
              <option value="Other" />
            </datalist>

            <FormField
              label="Unit of Measure"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            />

            <FormField
              label="Stock Quantity"
              type="number"
              value={form.stock_qty}
              onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
            />

            <FormField
              label="HSN Code"
              value={form.hsn_code}
              onChange={(e) => setForm({ ...form, hsn_code: e.target.value })}
            />

            <FormField
              label="Description"
              type="textarea"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Button onClick={handleSubmit}>
              {editingId ? "Update Material" : "Save Material"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setEditingId(null);
                setTab("list");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
